import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const STATUS_CLS = { confirmed: "bg-[#E7F0E9] text-[#1F5130]", pending: "bg-[#FFFBEB] text-[#92400E]", unsubscribed: "bg-[#EFEFEC] text-[var(--ink-3)]" };

const exportCsv = (subs) => {
  const rows = [["email", "status", "signed_up", "confirmed"], ...subs.map((s) => [s.email, s.status, s.created_at, s.confirmed_at || ""])];
  const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "ukdiscovered-subscribers.csv" });
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function AdminSubscribers() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/subscribers").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email}?`)) return;
    await api.delete(`/admin/subscribers/${s.id}`);
    toast.success("Subscriber removed");
    load();
  };

  if (!data) return <p className="meta">Loading</p>;
  return (
    <div data-testid="admin-subscribers-page">
      <div className="flex items-end justify-between">
        <div><p className="meta">Newsletter</p><h1 className="font-display text-4xl font-bold mt-1">Subscribers</h1></div>
        <button onClick={() => exportCsv(data.subscribers)} className="inline-flex items-center gap-2 border border-[#CFCFCB] bg-white font-ui text-sm px-4 py-2.5 hover:border-[var(--ink)] transition-colors" data-testid="admin-subscribers-export-button">
          <Download size={15} /> Export CSV
        </button>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {["confirmed", "pending", "unsubscribed"].map((k) => (
          <div key={k} className="bg-white border border-[var(--rule)] p-5" data-testid={`subscribers-count-${k}`}>
            <p className="meta">{k}</p><p className="mt-2 font-display text-4xl font-bold tabular-nums">{data.counts[k]}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 font-ui text-xs text-[var(--ink-3)]">Only confirmed subscribers receive emails. Send an article from its editor page using “Send to subscribers”.</p>
      <div className="mt-6 bg-white border border-[var(--rule)]" data-testid="admin-subscribers-list">
        {data.subscribers.length === 0 && <p className="p-6 italic text-[var(--ink-3)]">No sign-ups yet.</p>}
        {data.subscribers.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-[var(--rule)] last:border-b-0" data-testid={`subscriber-row-${s.id}`}>
            <span className="flex-1 min-w-[200px] font-ui text-sm">{s.email}</span>
            <span className="font-ui text-xs text-[var(--ink-3)]">Joined {formatDate(s.created_at)}</span>
            <span className={`font-ui text-[11px] uppercase tracking-wider px-2 py-0.5 ${STATUS_CLS[s.status]}`}>{s.status}</span>
            <button onClick={() => remove(s)} className="text-[var(--ink-3)] hover:text-[var(--accent)]" aria-label="Remove" data-testid={`subscriber-delete-${s.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
